// src/components/timetable/modern/useModernTimetableTypes.ts
//
// Ported from the mobile app's useTimetableTypes.ts. Swaps the mobile
// axios instance + useUserStore for the web app's apiClient +
// useSchoolDetailsStore (same session source TimeSlotsManager.tsx and
// AddTimetableEntryModal.tsx already use).

import { useEffect, useRef, useState } from 'react';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';
import { TimetableTypeOption } from './modernTimetableTypes';

// Shape returned per item by GET /timetable-types.
// NOTE: field names assumed to match the rest of the API's snake_case
// convention (id, name, status, session, session_id) — same assumption
// the mobile hook makes; double check against the Go
// models.TimetableType JSON tags if this comes back empty.
interface TimetableTypeResponse {
  id:         string;
  name:       string;
  status:     string;
  session:    string;
  session_id: string;
}

interface TimetableTypesApiResponse {
  status:  string;
  message?: string;
  data:    TimetableTypeResponse[];
}

interface UseModernTimetableTypesReturn {
  typeOptions: TimetableTypeOption[];
  loading:     boolean;
  error:       string | null;
  refetch:     () => void;
}

export const useModernTimetableTypes = (): UseModernTimetableTypesReturn => {
  const currentSessionId = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.id);

  const [typeOptions, setTypeOptions] = useState<TimetableTypeOption[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  // Cache refs to avoid refetching on every re-render for the same session
  const hasFetched    = useRef(false);
  const lastSessionId = useRef<string | null>(null);

  const fetchTypes = async () => {
    if (!currentSessionId) return;
    try {
      setLoading(true);
      setError(null);

      // NOTE: no leading '/' or '/api' prefix — matches TimeSlotsManager's
      // working `apiClient.get('timeslots', ...)` convention, i.e. the
      // web apiClient's baseURL already includes the API root.
      const res = await apiClient.get<TimetableTypesApiResponse>('timetable-types', {
        params: { session_id: currentSessionId },
      });

      if (res.data.status === 'success') {
        const types = res.data.data ?? [];
        const options: TimetableTypeOption[] = types.map((t) => ({
          label:  t.name,
          value:  t.id,
          status: t.status,
        }));
        setTypeOptions(options);
      } else {
        setError(res.data.message ?? 'Failed to fetch timetable types');
      }

      hasFetched.current = true;
      lastSessionId.current = currentSessionId;
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to fetch timetable types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current && lastSessionId.current === currentSessionId && typeOptions.length > 0) return;
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  // Reset cache when session changes
  useEffect(() => {
    if (lastSessionId.current && lastSessionId.current !== currentSessionId) {
      hasFetched.current = false;
      setTypeOptions([]);
    }
  }, [currentSessionId]);

  return { typeOptions, loading, error, refetch: fetchTypes };
};

export default useModernTimetableTypes;
